import PropTypes from 'prop-types';
import { isObject } from 'lodash-es';
import { RowManager } from '../../../../utils/row-manager';
import { BaseFormFieldContainer } from '../FormFieldContainer';
import { OverridableWrapper } from '../InputField';
import { AddRowButton, DeleteRowButton } from '../sharedComponents';

export const inlineFieldGroupClasses = 'flex flex-wrap gap-x-5 gap-y-2.5';

function extractErrorMessage(error) {
    return isObject(error) ? error.message : error;
}

export function GenericRowField({
    name: fieldGroupName,
    fields,
    emptyRow,
    maxRows,
    value,
    onChange,
    disabled,
    error,
    Manager = RowManager,
    extraConfig,
}) {
    const { rows, manager, rowLimitReached, showDeleteButtons } = Manager.useRowManager(
        value,
        onChange,
        maxRows,
        emptyRow,
        extraConfig
    );

    return (
        <div className="flex flex-col gap-y-2.5">
            {rows.map((row, index) => (
                <div
                    key={index}
                    className={inlineFieldGroupClasses}
                    data-testid={`${fieldGroupName}-row-${index}`}
                >
                    {fields.map(({ name: fieldName, label, render, helpText }) => {
                        const name = `${fieldGroupName}_${fieldName}_${index}`;
                        const id = `id_${name}`;

                        return (
                            <BaseFormFieldContainer
                                name={name}
                                label={label}
                                key={fieldName}
                                error={extractErrorMessage(error?.[index]?.[fieldName])}
                                helpText={helpText}
                                inline={true}
                            >
                                <OverridableWrapper
                                    overrideName={fieldGroupName}
                                    disabled={disabled}
                                    render={(props) =>
                                        render({
                                            ...props,
                                            index,
                                            id,
                                            value: row[fieldName] ?? '',
                                            onChange: (e) => {
                                                let newValue = e.target.value;

                                                if (newValue === '') {
                                                    newValue = null;
                                                } else if (e.target.type === 'number') {
                                                    newValue = e.target.valueAsNumber;
                                                }

                                                manager.changeRow(index, fieldName, newValue);
                                            },
                                        })
                                    }
                                />
                            </BaseFormFieldContainer>
                        );
                    })}
                    {showDeleteButtons && (
                        <DeleteRowButton manager={manager} index={index} disabled={disabled} />
                    )}
                </div>
            ))}

            {!rowLimitReached && (
                <AddRowButton manager={manager} disabled={disabled} className="w-fit" />
            )}
        </div>
    );
}

GenericRowField.propTypes = {
    name: PropTypes.string.isRequired,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
    emptyRow: PropTypes.any.isRequired,
    maxRows: PropTypes.number.isRequired,
    value: PropTypes.any.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool.isRequired,
    error: PropTypes.array,
    // Should be a subclass of RowManager, but PropTypes does not have a
    // fancy checker for that. Once we have TypeScript in place, thing like
    // this could be solved much better.
    Manager: PropTypes.func,
    extraConfig: PropTypes.object,
};
